import React, { useState } from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'

export interface QuestionnaireGroupSummary {
  questionnaireId: number
  title: string
  activeAssignment: boolean
  lastSubmissionDate: string | null
  totalSubmissions: number
}

export interface Assignment {
  id: number
  questionnaireTitle: string
  questionnaireHasScoring: boolean
  startDate: string
  scheduledEndDate: string
  terminatedOn: string | null
  isActive: boolean
  assignedByProviderName: string
  submissionCount: number
  latestScore?: number | null
}

// types for questions ------
export type QuestionTypeLabel =
  | 'boolean'
  | 'scale'
  | 'multiple_choice'
  | 'free_text'
  | 'numeric'

// Stored as JSON in the backend BranchRules column; e.g. { "yes": { "skipTo": 3 }, "no": { "skipToEnd": true } }
export type BranchRules = Record<string, BranchAction>
export interface BranchAction {
  skipTo?: number // sortOrder to jump to
  skipToEnd?: boolean // skip all remaining questions
}

// Option shapes for question type: 'scale' | 'multiple_choice' | 'free_text' | 'numeric'
export interface ScaleOptions {
  min: number
  max: number
  minLabel?: string
  maxLabel?: string
  step?: number
}
export interface ChoiceItem {
  value: string
  label: string
  points?: number
}
export interface MultipleChoiceOptions {
  choices: ChoiceItem[]
  allowMultiple?: boolean
}
export interface FreeTextOptions {
  maxLength?: number
  placeholder?: string
}

/*  eg. For a scale question:
        {
            questionType: 'scale',
            options: { min: 1, max: 5, minLabel: 'Never', maxLabel: 'Always' }
        } 
*/
export interface NumericOptions {
  min?: number
  max?: number
  unit?: string
  placeholder?: string
}

export type QuestionOptions =
  | ScaleOptions
  | MultipleChoiceOptions
  | FreeTextOptions
  | NumericOptions

export interface Question {
  id: number
  sortOrder: number
  content: string
  instruction?: string
  questionType: QuestionTypeLabel
  options?: QuestionOptions
  branchRules?: BranchRules
  isRequired: boolean
  points?: number
}
// ------ types for questions

export interface SubmissionAnswer {
  sortOrder: number
  questionType: string
  questionContent: string
  answerValue: string
  questionPoints?: number
  questionOptions?: any
}

export interface Submission {
  id: number
  completedAt: string
  isIgnored: boolean
  score?: number
  answers: SubmissionAnswer[]
}

export interface StartAssignmentRequest {
  questionnaireId: number
  patientId: number
  recurrenceWeeks: number
  recurrenceIntervalDays: number
}

export interface RestartAssignmentRequest {
  oldAssignmentId: number
  questionnaireId: number
  patientId: number
  recurrenceWeeks: number
  recurrenceIntervalDays: number
}

export interface QuestionnaireContextInterface {
  questionnaireGroupSummaries: QuestionnaireGroupSummary[]
  questionnaireSubmissions: {
    questions: Question[]
    submissions: Submission[]
  }
  getQuestionnaireGroupSummaries: (
    patientId: number
  ) => Promise<QuestionnaireGroupSummary[]>
  getPatientAssignments: (patientId: number) => Promise<Assignment[]>
  getAssignmentSubmissions: (assignmentId: number) => Promise<{
    questions: Question[]
    submissions: Submission[]
  }>
  startAssignment: (request: StartAssignmentRequest) => Promise<number | null>
  stopAssignment: (assignmentId: number) => Promise<number | null>
  restartAssignment: (
    request: RestartAssignmentRequest
  ) => Promise<number | null>
}

export const QuestionnaireContext =
  React.createContext<QuestionnaireContextInterface | null>(null)

export const QuestionnaireProvider = ({ children }: any) => {
  const { fetch } = useAxios()

  const [questionnaireGroupSummaries, setQuestionnaireGroupSummaries] =
    useState([])

  const [questionnaireSubmissions, setQuestionnaireSubmissions] = useState<{
    questions: Question[]
    submissions: Submission[]
  }>({
    questions: [],
    submissions: [],
  })

  const getQuestionnaireGroupSummaries = async (
    patientId: number
  ): Promise<QuestionnaireGroupSummary[]> => {
    try {
      const { data } = await fetch({
        path: `Questionnaire/GetQuestionnaireGroupSummaries?patientId=${patientId}`,
        methodType: 'GET',
      })
      setQuestionnaireGroupSummaries(data ?? [])
      return data ?? []
    } catch (error) {
      console.log('Error fetching questionnaire summaries: ', error)
      return []
    }
  }

  const getPatientAssignments = async (
    patientId: number
  ): Promise<Assignment[]> => {
    try {
      const { data } = await fetch({
        path: `Questionnaire/GetPatientAssignments?patientId=${patientId}`,
        methodType: 'GET',
      })
      return data ?? []
    } catch (error) {
      console.log('Error fetching assignments: ', error)
      return []
    }
  }

  const getAssignmentSubmissions = async (
    assignmentId: number
  ): Promise<{
    questions: Question[]
    submissions: Submission[]
  }> => {
    try {
      const { data } = await fetch({
        path: `Questionnaire/GetAssignmentSubmissions?assignmentId=${assignmentId}`,
        methodType: 'GET',
      })
      setQuestionnaireSubmissions(data ?? [])
      return data ?? []
    } catch (error) {
      console.log('Error fetching submissions: ', error)
      return {
        questions: [],
        submissions: [],
      }
    }
  }

  const startAssignment = async (
    request: StartAssignmentRequest
  ): Promise<number | null> => {
    try {
      const { data } = await fetch({
        path: 'Questionnaire/StartQuestionnaireAssignment',
        methodType: 'POST',
        body: JSON.stringify(request),
      })
      return data
    } catch (error) {
      console.log('Error starting assignment: ', error)
      return null
    }
  }

  const stopAssignment = async (
    assignmentId: number
  ): Promise<number | null> => {
    try {
      const { data } = await fetch({
        path: `Questionnaire/StopQuestionnaireAssignment?assignmentId=${assignmentId}`,
        methodType: 'PUT',
      })
      return data
    } catch (error) {
      console.log('Error stopping assignment: ', error)
      return null
    }
  }

  const restartAssignment = async (
    request: RestartAssignmentRequest
  ): Promise<number | null> => {
    try {
      const { data } = await fetch({
        path: 'Questionnaire/RestartQuestionnaireAssignment',
        methodType: 'POST',
        body: JSON.stringify(request),
      })
      return data
    } catch (error) {
      console.log('Error restarting assignment: ', error)
      return null
    }
  }

  return (
    <QuestionnaireContext.Provider
      value={{
        questionnaireGroupSummaries,
        questionnaireSubmissions,
        getQuestionnaireGroupSummaries,
        getPatientAssignments,
        getAssignmentSubmissions,
        startAssignment,
        stopAssignment,
        restartAssignment,
      }}
    >
      {children}
    </QuestionnaireContext.Provider>
  )
}
